import React from 'react';

import { useSharedTranslation } from '../hooks/useSharedTranslation';

interface Props {
  page: number;
  pageCount: number;
  firstPage: boolean;
  lastPage: boolean;
  onPageChange: (page: number) => void;
  namespace?: string;
}

interface PaginationEntry {
  key: string;
  page: number | null; // null = '...' gap
}

// Nearby pages (current ± 2) plus a first/last-3 window, with '...' gaps
// for skipped ranges.
const getPaginationArray = (page: number, pageCount: number): PaginationEntry[] => {
  const nearbyLimit = 2;
  const farLimit = 3;

  const keep = new Set<number>();
  for (let i = 1; i <= pageCount; i++) {
    if (
      i <= farLimit ||
      i > pageCount - farLimit ||
      (i >= page - nearbyLimit && i <= page + nearbyLimit)
    ) {
      keep.add(i);
    }
  }

  const pages: PaginationEntry[] = [];
  let previous = 0;
  for (let i = 1; i <= pageCount; i++) {
    if (!keep.has(i)) continue;
    if (previous !== 0 && i - previous > 1) {
      pages.push({ key: `ellipsis-${previous}`, page: null });
    }
    pages.push({ key: `page-${i}`, page: i });
    previous = i;
  }

  return pages;
};

const Pagination = ({
  page,
  pageCount,
  firstPage,
  lastPage,
  onPageChange,
  namespace,
}: Props): JSX.Element | null => {
  const { t } = useSharedTranslation(namespace);

  if (pageCount <= 1) return null;

  const pages = getPaginationArray(page, pageCount);

  const baseClasses = 'inline-flex items-center justify-center min-w-[36px] h-9 px-2 -ml-px border '
    + 'border-sapphire-100 text-sm font-semibold cursor-pointer select-none';

  return (
    <nav aria-label={t('common.pagination.ariaLabel')} className='my-5 flex justify-center'>
      <ul className='flex flex-wrap p-0 m-0 list-none'>
        {!firstPage && (
          <li>
            <button
              type='button'
              aria-label={t('common.previous')}
              onClick={(): void => onPageChange(page - 1)}
              className={`${baseClasses} rounded-l-full bg-sapphire text-white hover:bg-sapphire-700`}
            >
              <span className='hidden sm:inline' aria-hidden='true'>&lsaquo;</span>
              <span className='sm:hidden'>{t('common.previous')}</span>
            </button>
          </li>
        )}
        {pages.map((entry) => (
          <li key={entry.key} className='hidden sm:block'>
            {entry.page === null ? (
              <span className={`${baseClasses} bg-neutral-100 text-neutral-400 cursor-default`}>
                &hellip;
              </span>
            ) : (
              <button
                type='button'
                aria-label={t('common.pagination.pageAria', { page: entry.page })}
                aria-current={entry.page === page ? 'page' : undefined}
                onClick={(): void => {
                  // entry.page !== null here (the ternary above already excluded that case),
                  // but TS doesn't retain that narrowing through this closure.
                  if (entry.page !== page) onPageChange(entry.page as number);
                }}
                className={
                  entry.page === page
                    ? `${baseClasses} bg-white text-sapphire cursor-default`
                    : `${baseClasses} bg-sapphire text-white hover:bg-sapphire-700`
                }
              >
                {entry.page}
              </button>
            )}
          </li>
        ))}
        {!lastPage && (
          <li>
            <button
              type='button'
              aria-label={t('common.next')}
              onClick={(): void => onPageChange(page + 1)}
              className={`${baseClasses} rounded-r-full bg-sapphire text-white hover:bg-sapphire-700`}
            >
              <span className='hidden sm:inline' aria-hidden='true'>&rsaquo;</span>
              <span className='sm:hidden'>{t('common.next')}</span>
            </button>
          </li>
        )}
      </ul>
    </nav>
  );
};

export { Pagination };
